import { useEffect, useMemo, useRef, useState } from 'react';
import { useActionData, redirect, useLoaderData, useNavigate, useRevalidator, useRouteLoaderData, useSubmit } from 'react-router';
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router';
import { loadHomeData, type HomeLoaderData } from '../lib/homeData';
import { useSettled } from '../lib/useSettled';
import type { RootLoaderData } from './rootLayout';
import { api } from '../lib/api';
import {
  buildFilmProject,
  buildSaasProject,
  projectPath,
  buildMarketingProject,
  buildUpscaleProject,
  buildVfxProject,
  type NewProjectInput
} from '../lib/project';
import { showLegacyHost } from '../lib/workspaceHost';
import { seriesApi, seriesPath } from '../lib/series';
import NewProjectDialog, { type ProjectKind } from '../components/NewProjectDialog';
import StartProductionDialog, { type DepartmentId } from '../components/StartProductionDialog';
import { agencyApi, startMarketingIntake, type Intake, type UploadCache } from '../features/studio/marketing/agencyApi';
import ConfirmDialog from '../components/ConfirmDialog';
import FloorHeader from '../components/FloorHeader';
import type { Project } from '../lib/types';
import ProductionHero from '../features/projects/ProductionHero';
import ProductionSlate from '../features/projects/ProductionSlate';
import ProductionPipeline from '../features/projects/ProductionPipeline';
import '../ui/styles/components.css';
import '../features/projects/home.css';
import '../features/series/series.css';

export function homeLoader({ request }: LoaderFunctionArgs): HomeLoaderData {
  return loadHomeData(request.signal);
}

export async function homeAction({ request }: ActionFunctionArgs) {
  const form = await request.formData();
  const intent = String(form.get('intent') || '');

  if (intent === 'logout') return redirect('/signout');

  if (intent === 'delete-project') {
    // Throwing here reaches the root error element and replaces the entire
    // productions view with a crash card. A delete that fails is recoverable,
    // so it is reported in place instead.
    try {
      await api.deleteProject(String(form.get('projectId') || ''));
      return { ok: true };
    } catch (cause) {
      return { ok: false, error: cause instanceof Error ? cause.message : 'Could not delete that project.' };
    }
  }

  return { ok: false };
}

export default function Home() {
  const data = useLoaderData() as HomeLoaderData;
  const { auth } = useRouteLoaderData('root') as RootLoaderData;
  const projectResult = useSettled(data.projects);
  /* Episodes live inside their series overview; the grid shows the series once. */
  const projects = useMemo(() => (projectResult.data || []).filter(project => { const link = project.seriesLink as { role?: string; archived?: boolean } | undefined; return link?.role !== 'episode' && !link?.archived; }), [projectResult.data]);
  /* Archived series keep everything; they leave the grid and wait here to be restored. */
  const archivedSeries = useMemo(() => (projectResult.data || []).filter(project => { const link = project.seriesLink as { role?: string; archived?: boolean } | undefined; return link?.role === 'series' && link.archived; }), [projectResult.data]);
  const navigate = useNavigate();
  const submit = useSubmit();
  const revalidator = useRevalidator();
  const startTriggerRef = useRef<HTMLButtonElement>(null);
  const [dialogKind, setDialogKind] = useState<ProjectKind | null>(null);
  const [startOpen, setStartOpen] = useState(false);
  const [error, setError] = useState('');
  const [pendingDelete, setPendingDelete] = useState<Project | null>(null);
  const [pendingConvert, setPendingConvert] = useState<Project | null>(null);
  const [importing, setImporting] = useState(false);
  const actionData = useActionData() as { ok?: boolean; error?: string } | undefined;

  useEffect(() => {
    showLegacyHost(false);
  }, []);

  const firstName = (auth.user?.name || 'Producer').split(' ')[0];

  const openProject = (project: Project) => navigate(projectPath(project));

  const createProject = async (kind: ProjectKind, input: NewProjectInput) => {
    const builder =
      kind === 'saas' ? buildSaasProject : kind === 'vfx' ? buildVfxProject : kind === 'marketing' ? buildMarketingProject : kind === 'upscale' ? buildUpscaleProject : buildFilmProject;
    const created = await api.createProject(builder({ ...input, ownerName: auth.user?.name }));
    if (kind === 'film' && input.estimatePackage) await api.startFilmPlan(created.id, { packageId: input.estimatePackage }).catch(() => null);
    setDialogKind(null);
    navigate(projectPath(created));
  };

  const marketingDraft = useRef<{ project: Project; turnId: string; uploads: UploadCache } | null>(null);
  const [businesses, setBusinesses] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    if (dialogKind !== 'marketing') return;
    let active = true;
    agencyApi.businesses().then(result => { if (active) setBusinesses(result.businesses); }).catch(() => {});
    return () => { active = false; };
  }, [dialogKind]);

  const createMarketingFromIntake = async (input: Intake) => {
    if (!marketingDraft.current) {
      const created = await api.createProject(buildMarketingProject({ name: 'New business', type: 'Social campaign', format: '1:1', runtimeMinutes: 1, idea: input.text, fps: 24, ownerName: auth.user?.name }));
      marketingDraft.current = { project: created, turnId: crypto.randomUUID(), uploads: new Map() };
    }
    const { project, turnId, uploads } = marketingDraft.current;
    try {
      const latest = await api.getProject(project.id);
      await startMarketingIntake(project.id, Number(latest.version), input, turnId, uploads);
    } catch (cause) {
      const reason = cause instanceof Error ? cause.message : 'Cue could not read the brief.';
      throw new Error(`Your project is saved, but Cue couldn't take the brief yet: ${reason} Press “Start with Cue” to try again${uploads.size ? ' — your photo is already uploaded and will not upload twice' : ''}. Cancel opens the saved project so you can continue there.`);
    }
    marketingDraft.current = null;
    setDialogKind(null);
    revalidator.revalidate();
    navigate(projectPath(project));
  };

  const convertToSeries = async (project: Project) => {
    setError('');
    try {
      const created = await seriesApi.convert(project.id);
      navigate(seriesPath(created.series.id));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not convert that production.');
    }
  };

  const restoreSeries = async (project: Project) => {
    setError('');
    try {
      await seriesApi.restore(String((project.seriesLink as { seriesId?: string }).seriesId || ''));
      revalidator.revalidate();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not restore that series.');
    }
  };

  /* A series leaves as one archive and is deleted from its overview, where every episode goes with it. */
  const seriesIdOf = (project: Project) => { const link = project.seriesLink as { role?: string; seriesId?: string } | undefined; return link?.role === 'series' && link.seriesId ? link.seriesId : null; };
  const exportProject = (project: Project) => { const seriesId = seriesIdOf(project); if (seriesId) seriesApi.exportArchive(seriesId); else api.exportArchive(project.id); };
  const deleteProject = (project: Project) => { const seriesId = seriesIdOf(project); if (seriesId) navigate(seriesPath(seriesId), { state: { deleteSeries: true } }); else setPendingDelete(project); };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    submit({ intent: 'delete-project', projectId: pendingDelete.id }, { method: 'post' });
    setPendingDelete(null);
  };

  const importArchive = async (file: File) => {
    setImporting(true);
    setError('');
    try {
      const imported = await api.importProjectArchive(file);
      revalidator.revalidate();
      navigate(projectPath(imported));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not import that project.');
    } finally {
      setImporting(false);
    }
  };

  const startDepartment = (id: DepartmentId) => {
    setStartOpen(false);
    if (id === 'talk') {
      navigate('/generator?surface=talk');
      return;
    }
    if (id === 'viral') {
      navigate('/viral');
      return;
    }
    if (id === 'generator') {
      navigate('/generator');
      return;
    }
    setDialogKind(id);
  };

  return (
    <div className="bs-theme studio-home">
      <FloorHeader active="productions" user={auth.user} onError={setError} />

      <main className="studio-home-content">
        <ProductionHero name={firstName} startRef={startTriggerRef} startOpen={startOpen} onStart={() => setStartOpen(true)} />
        <ProductionSlate projects={projects} loading={projectResult.loading} importing={importing} error={projectResult.error} actionError={error || actionData?.error} onRetry={() => revalidator.revalidate()} onStart={() => setStartOpen(true)} onOpen={openProject} onJobs={project => navigate(`/projects/${encodeURIComponent(project.id)}/jobs`)} onExport={exportProject} onImport={file => void importArchive(file)} onDelete={deleteProject} onConvert={project => setPendingConvert(project)} />
        {archivedSeries.length > 0 && (
          <section className="studio-home-archived" aria-label="Archived series">
            <p className="bs-eyebrow">Archived series · <span className="bs-mono">{archivedSeries.length}</span></p>
            <ul>
              {archivedSeries.map(project => (
                <li key={project.id}>
                  <strong className="bs-small">{project.name}</strong>
                  <span className="bs-small bs-muted">Everything is kept. Paid work is paused.</span>
                  <button type="button" className="studio-story-revise-link" onClick={() => navigate(seriesPath(String((project.seriesLink as { seriesId?: string }).seriesId || '')))}>View</button>
                  {!['reviewer', 'client'].includes(String(project.collabRole || '')) && <button type="button" className="studio-story-revise-link" onClick={() => void restoreSeries(project)}>Restore</button>}
                </li>
              ))}
            </ul>
          </section>
        )}
        <ProductionPipeline />
      </main>

      {pendingDelete ? (
        <ConfirmDialog
          title="Delete this production?"
          body={
            <>
              <strong>{pendingDelete.name}</strong> and everything generated inside it — shots, takes, locked
              references and delivered cuts — are removed permanently. This cannot be undone.
            </>
          }
          confirmLabel="Delete production"
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      ) : null}

      {pendingConvert ? (
        <ConfirmDialog
          title="Convert to a series?"
          body={
            <>
              <strong>{pendingConvert.name}</strong> becomes the source of a new Microdrama series. Its answers start the Series Deal
              Memo and its screenplay is imported for the Master Story — nothing is approved for you. When you open Episode 1, this
              production becomes that episode as it is: script, takes and approvals are kept.
            </>
          }
          confirmLabel="Convert to series"
          tone="default"
          onConfirm={() => { const project = pendingConvert; setPendingConvert(null); void convertToSeries(project); }}
          onCancel={() => setPendingConvert(null)}
        />
      ) : null}

      {startOpen ? (
        <StartProductionDialog
          onPick={startDepartment}
          productionLocked={auth.productionAllowed === false}
          vfxAllowed={auth.vfxAllowed === true}
          onSeePlans={() => navigate('/pricing?plan=creator')}
          onClose={() => {
            setStartOpen(false);
            startTriggerRef.current?.focus();
          }}
        />
      ) : null}

      {dialogKind ? (
        <NewProjectDialog
          kind={dialogKind}
          onClose={() => { if (marketingDraft.current) { const pending = marketingDraft.current.project; marketingDraft.current = null; setDialogKind(null); revalidator.revalidate(); navigate(projectPath(pending)); return; } setDialogKind(null); }}
          businesses={businesses}
          onCreateMarketing={createMarketingFromIntake}
          onCreate={async input => {
            await createProject(dialogKind, input);
            revalidator.revalidate();
          }}
          onCreateSeries={async input => {
            const created = await seriesApi.create(input);
            setDialogKind(null);
            navigate(seriesPath(created.series.id));
          }}
        />
      ) : null}
    </div>
  );
}
